import styled, { css } from "styled-components";
import { HiHome, HiMenu } from "react-icons/hi";
import { Box, Flex } from "rebass/styled-components";
import { Link } from "react-router-dom";

import { ReactComponent as HeaderLogo } from "../../../assets/images/logo/headerLogo.svg";

import {
  PAGE_GUTTER,
  PAGE_MAX_WIDTH,
} from "MVVM/Views/LandingPages/styledComponents";
// Matches the app's pages (MyLibrary, Match History), not the homepage, which
// also caps at 1480 and centres.
const NAV_GUTTER = "60px";
const NAV_GUTTER_NARROW = "24px";

export const NavBar = styled.header<{ $standalone?: boolean }>`
  /* The category row. Lighter than the strip above it: the strip frames the
     site, so it reads as the outer chrome and this as the nav proper. */
  background: ${({ theme }) => theme.colors.navy500};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.3);
  /* Standalone owns its gutter via NavInner, so no outer offset. */
  padding: 18px 0;
  /* Sticky, not relative: the window scrolls rather than MainContent, so
     relative carried the bar off screen on long lists. */
  position: sticky;
  top: 0;
  z-index: 101;
`;

// On landing pages this matches the page's own frame, so the nav sits on the
// same edge as the sections below it.
export const NavInner = styled(Flex)<{ $standalone?: boolean }>`
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
  max-width: ${({ $standalone }) => ($standalone ? PAGE_MAX_WIDTH : "none")};
  padding: 0 ${({ $standalone }) => ($standalone ? PAGE_GUTTER : NAV_GUTTER)};
  width: 100%;

  /* Holds only the category row, which hides at this width, so the whole row
     goes with it. */
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

/* Super nav: the permanent top strip. Brand, search and identity. */

// NavBar has no horizontal padding, so this only cancels the vertical. The
// gutter lives on SuperNavInner.
export const SuperNavStrip = styled.div<{ $standalone?: boolean }>`
  background: ${({ theme }) => theme.colors.navy600};
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  /* Above the mega menu's 100. The search field creates its own stacking
     context at z-index 5, so without this its dropdown lost to a panel opened
     from the row below and rendered behind it. Covers the account menu too. */
  position: relative;
  z-index: 110;
  margin: -18px 0 14px;
  padding: 11px 0;

  /* Below medium the category row is hidden, so NavInner has nothing in it and
     the strip absorbs the bar's remaining padding rather than leaving a band. */
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    margin: -18px 0 -18px;
  }
`;

export const SuperNavInner = styled(Flex)<{ $standalone?: boolean }>`
  align-items: center;
  gap: 16px;
  margin: 0 auto;
  max-width: ${({ $standalone }) => ($standalone ? PAGE_MAX_WIDTH : "none")};
  padding: 0 ${({ $standalone }) => ($standalone ? PAGE_GUTTER : NAV_GUTTER)};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 0
      ${({ $standalone }) => ($standalone ? PAGE_GUTTER : NAV_GUTTER_NARROW)};
  }
`;

// Owns the right-push. On the links it collapsed with them when the row ran
// short, dropping the account cluster back against the search field.
export const SuperNavEnd = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 16px;
  margin-left: auto;
`;

// DOM order is Teams, Matches, Library, Profile, reading right to left from the
// avatar, so shedding from the front drops the least-used first. The account
// panel carries all four below standard.
export const SuperNavLinks = styled.nav`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 2px;

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    a:nth-child(1) {
      display: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.theater}) {
    a:nth-child(2) {
      display: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

// Quieter than the category row on purpose, chalk against its ice. It takes the
// same wide-screen step the category row does: without it the bar grew above
// 2160px while these stayed put, which read as a mistake rather than hierarchy.
export const SuperNavLink = styled(Link)<{ $active?: boolean }>`
  align-items: center;
  background: transparent;
  border-radius: 6px;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.envy : theme.colors.chalk};
  display: flex;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  gap: 7px;
  padding: 5px 9px;
  text-decoration: none;
  white-space: nowrap;

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    font-size: 16px;
    padding: 6px 11px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha06};
    color: ${({ theme, $active }) =>
      $active ? theme.colors.envy : theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  /* Bundled SVG assets carry no fill of their own. */
  svg {
    fill: currentColor;
    height: 15px;
    width: 15px;
  }

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    svg {
      height: 17px;
      width: 17px;
    }
  }
`;

/**
 * The bell and the account menu are one cluster, so they sit tight together.
 * The trial button is a separate offer rather than part of your identity, so it
 * keeps its distance via its own margin instead of an even gap across all three.
 */
export const SuperNavRight = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 10px;
`;

export const LeftGroup = styled(Flex)`
  align-items: center;
  flex: 1;
  gap: 22px;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    gap: 14px;
  }
`;

export const NavDivider = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  flex-shrink: 0;
  height: 22px;
  /* The shortcut links carry their own horizontal padding, so the flex gap
     lands on top of it on this side and flush against the bell on the other.
     Matching that padding here puts equal air around the rule. */
  margin-right: 9px;
  width: 1px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

export const CategoryRow = styled.nav`
  align-items: stretch;
  display: flex;
  /* The primary nav is the last thing that should give up space; the search
     slot yields instead. */
  flex-shrink: 0;
  gap: 4px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

/**
 * Shared by the trigger and the plain link so their size, weight and hit area
 * cannot drift apart. Full-height padding with a matching negative margin, so
 * there is no diagonal dead zone between a trigger and the panel below it.
 */
const categoryItemStyle = css`
  align-items: center;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2px;
  margin: -18px 0;
  padding: 18px 14px;
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  /* A step up on very wide displays, where 1080p sizing reads small. Kept
     modest and matched by the panel below, so the bar does not tower over the
     menu it opens. */
  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    font-size: 18px;
    padding: 18px 18px;
  }
`;

export const CategoryTrigger = styled.button<{ $open?: boolean }>`
  ${categoryItemStyle}
  background: none;
  border: none;
  gap: 5px;
  /* Resting is slightly muted, hover brightens to full ice, open goes envy.
     The current route deliberately does NOT tint the trigger: green in the
     global bar read as noise. Route state still ships as aria-current, and the
     panel's own items highlight normally. */
  color: ${({ theme, $open }) =>
    $open ? theme.colors.envy : theme.colors.iceAlpha75};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: ${({ theme, $open }) =>
      $open ? theme.colors.envy : theme.colors.ice};
  }

  /* Direct child only: the leading icon is wrapped, so the chevron's rotation
     cannot reach it. */
  > svg {
    height: 15px;
    margin-top: 3px;
    transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
    transition: transform 150ms ease;
    width: 15px;
  }

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    svg {
      height: 17px;
      width: 17px;
    }
  }
`;

// A plain destination in the bar, so no chevron and no panel.
export const CategoryLink = styled(Link)`
  ${categoryItemStyle}
  color: ${({ theme }) => theme.colors.iceAlpha75};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

// Sits in the strip beside the brand. Hidden below medium, where NavSearchBar
// shows its own mobile affordance instead.
export const SearchSlot = styled(Box)`
  flex: 0 1 330px;
  min-width: 170px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: none;
  }
`;

// Flex, not inline: an inline link puts the SVG on a text baseline and reserves
// descender space under it, throwing the logo off centre.
export const LogoLink = styled(Link)`
  align-items: center;
  display: flex;
`;

export const StyledHeaderLogo = styled(HeaderLogo)`
  height: 24px;
  width: auto;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    height: 20px;
  }
`;

/**
 * Icon-only, at the head of the category row. The logo already goes home, but a
 * brand mark is not an obvious affordance to everyone, and a worded item here
 * competed with the categories for reading order.
 */
export const HomeLink = styled(Link)`
  align-items: center;
  align-self: stretch;
  color: ${({ theme }) => theme.colors.iceAlpha75};
  display: flex;
  /* Pulled back by its own padding so the glyph sits on the gutter, in line
     with the logo above it, rather than 12px inside it. */
  margin: -18px 0 -18px -12px;
  padding: 18px 12px;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: -2px;
  }

  svg {
    height: 19px;
    width: 19px;
  }

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    padding: 18px 16px;

    svg {
      height: 22px;
      width: 22px;
    }
  }
`;

export const StyledHiHome = styled(HiHome)``;

export const StyledHiMenu = styled(HiMenu)`
  height: 24px;
  width: 24px;
`;

export const BurgerButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: none;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    display: flex;
  }
`;

/* Mega menu */

/**
 * Positioning shell only, so the card below can size itself. It spans the bar
 * but paints nothing: the visible surface is PanelInner.
 */
export const Panel = styled.div`
  /* NavBar is the containing block, so 0 already reaches the viewport edges. */
  left: 0;
  /* The rail art bleeds out to the viewport on purpose, so the panel clips it
     rather than PanelInner. */
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 100;

  @media (prefers-reduced-motion: no-preference) {
    animation: navPanelIn 120ms ease-out;
  }

  @keyframes navPanelIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
  }
`;

// Menu A, editorial split: left third pitches the category, right side is the
// two-column link grid.
// The panel's visible surface, full bleed with the content on the nav's gutter.
export const PanelInner = styled.div<{ $standalone?: boolean }>`
  /* Matches the category row it drops out of, so the panel reads as that row
     extending rather than a surface landing on top of it. */
  background: ${({ theme }) => theme.colors.navy500};
  /* Flat and edge to edge: rounded corners read as a floating slab at this
     width, and clip against screen edges with nothing to round against. */
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.35);
  display: grid;
  /* Every panel is the same height. The grid is two columns, so a four-item
     category runs two rows and a two-item one runs a single row, which made the
     panel jump as you moved across the triggers. */
  min-height: 196px;
  position: relative;
  z-index: 1;
  gap: 48px;
  grid-template-columns: minmax(240px, 1fr) 2fr;
  /* Same gutter as the bar above and the pages below, so the rail copy lines up
     with the logo and the triggers instead of sitting on its own. */
  margin: 0 auto;
  max-width: ${({ $standalone }) => ($standalone ? PAGE_MAX_WIDTH : "none")};
  padding: 26px ${({ $standalone }) => ($standalone ? PAGE_GUTTER : NAV_GUTTER)}
    30px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    padding: 26px
      ${({ $standalone }) => ($standalone ? PAGE_GUTTER : NAV_GUTTER_NARROW)}
      30px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

// The section's rail: a background filling the column and a glass object at the
// headline.
export const PanelPitch = styled.div<{ $art?: string; $glass?: string }>`
  border-right: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  padding-right: 40px;
  position: relative;

  /* Fills the rail behind the copy, bleeding out to the panel's own edges so it
     meets the bar above and the panel's bottom with no gap. Fades toward the
     divider rather than stopping on it, and again at its left edge. The content
     box is capped and centred, so the art runs well past it to the viewport and
     the panel clips it: without that there was a band of empty dark in front of
     the rail on a wide screen. */
  ${({ $art }) =>
    $art &&
    `
      &::before {
        background-image: url("${$art}");
        background-position: center;
        background-size: cover;
        bottom: -30px;
        content: "";
        left: -50vw;
        mask-image: linear-gradient(to right, transparent 0%, #000 32%, #000 62%, transparent 100%);
        -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 32%, #000 62%, transparent 100%);
        pointer-events: none;
        position: absolute;
        right: 0;
        top: -26px;
      }
    `}

  /* The section's glass object, sitting with the headline and bleeding a little
     past the rail's left edge. Behind the copy, so the wordmark stays legible
     where the two overlap. */
  ${({ $glass }) =>
    $glass &&
    `
      &::after {
        background-image: url("${$glass}");
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
        content: "";
        height: 72px;
        /* Tucked to the left of the wordmark, ending about where the copy
           begins, the way the mockup has it. The deeper left inset on the
           content is what makes room for this without the card clipping it. */
        left: -34px;
        pointer-events: none;
        position: absolute;
        top: 4px;
        width: 72px;
      }
    `}

  /* Copy rides above the artwork. */
  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
    border-right: none;
    padding-bottom: 24px;
    padding-right: 0;
  }
`;

export const PanelEyebrow = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

export const PanelHeadline = styled.h2`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 34px;

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    font-size: 40px;
  }
  font-style: italic;
  font-weight: 900;
  letter-spacing: -0.01em;
  margin: 8px 0 12px;
  text-transform: uppercase;
`;

export const PanelBlurb = styled.p`
  /* Brighter than chalk: this copy sits over the rail artwork, where chalk on a
     busy background was hard to read. */
  color: ${({ theme }) => theme.colors.iceAlpha75};
  font-size: 15px;

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    font-size: 17px;
  }
  line-height: 1.55;
  margin: 0 0 22px;
  max-width: 34ch;
`;

const panelCtaStyle = `
  border-radius: 8px;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 18px;
  text-decoration: none;
`;

export const PanelCta = styled(Link)<{ $accent?: string }>`
  ${panelCtaStyle}
  background: ${({ theme, $accent }) => $accent || theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    filter: brightness(1.12);
  }
`;

export const PanelCtaAnchor = styled.a<{ $accent?: string }>`
  ${panelCtaStyle}
  background: ${({ theme, $accent }) => $accent || theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    filter: brightness(1.12);
  }
`;

export const ItemGrid = styled.div`
  align-content: start;
  display: grid;
  gap: 4px 24px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    grid-template-columns: 1fr;
  }
`;

export const ItemTitle = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 600;

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    font-size: 18px;
  }
`;

// Matches the red "New" pill the sidebar and account settings already use.
export const ItemBadge = styled.span`
  background: ${({ theme }) => theme.colors.alert};
  border-radius: 100px;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  padding: 4px 8px;
  text-transform: uppercase;
`;

export const ItemDescription = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    font-size: 16px;
  }
  line-height: 1.45;
  /* Indented past the glyph so it hangs under the title, not the icon. */
  margin: -1px 0 0 28px;
`;

// Bare glyph, sitting inline with the title.
export const ItemIcon = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-shrink: 0;

  /* fill:currentColor matters for the bundled SVG assets: their paths carry no
     fill of their own, so they default to black and disappear on the dark bar.
     react-icons glyphs already do this, so it is a no-op for them. */
  svg {
    fill: currentColor;
    height: 20px;
    width: 20px;
  }
`;

// Icon and title share a line; the description sits under them.
export const ItemTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

const itemStyle = `
  border-radius: 8px;
  display: block;
  padding: 10px 12px 12px;
  text-decoration: none;
`;

/**
 * Hover only, no resting surface. The current route deliberately gets no tint
 * either, matching the category triggers, since a panel opened from the page
 * you are already on lit a row for no useful reason. Route state still ships as
 * aria-current.
 */
const itemHoverStyle = css`
  background: transparent;
  transition: background 0.15s ease;

  ${ItemTitle} {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha06};

    ${ItemTitle},
    ${ItemIcon} {
      color: ${({ theme }) => theme.colors.envy};
    }
  }
`;

export const ItemLink = styled(Link)`
  ${itemStyle}
  ${itemHoverStyle}
`;

export const ItemAnchor = styled.a`
  ${itemStyle}
  ${itemHoverStyle}
`;

/* Mobile sheet */

export const MobileDialog = styled.dialog`
  background: ${({ theme }) => theme.colors.navy600};
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  height: 100%;
  max-height: 100%;
  max-width: 100%;
  padding: 0;
  width: 100%;

  &::backdrop {
    background: ${({ theme }) => theme.colors.midnightAlpha80};
  }
`;

export const MobileHeader = styled(Flex)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  justify-content: flex-end;
  padding: 18px 20px;
`;

export const MobileCloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  padding: 4px;
`;

// Envy when open, like everything else in the nav. The section's own accent
// was tried here and read as five unrelated colours in one list; the artwork
// behind the rows already carries that identity.
export const AccordionTrigger = styled.button<{ $open?: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  color: ${({ theme, $open }) =>
    $open ? theme.colors.envy : theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  justify-content: space-between;
  letter-spacing: 1px;
  padding: 18px 20px;
  text-transform: uppercase;
  width: 100%;
`;

/**
 * Carries the section's rail artwork, the same images the desktop panel uses.
 * The drawer was flat navy with none of that identity; here the background sits
 * behind the rows and the glass object tucks into the corner, both well dimmed
 * so the links stay the thing you read.
 */
export const AccordionBody = styled(Box)<{ $art?: string; $glass?: string }>`
  padding: 8px 12px 16px;
  position: relative;

  ${({ $art }) =>
    $art &&
    `
      &::before {
        background-image: url("${$art}");
        background-position: center;
        background-size: cover;
        content: "";
        filter: brightness(0.62);
        inset: 0;
        mask-image: linear-gradient(to bottom, #000 40%, transparent 100%);
        -webkit-mask-image: linear-gradient(to bottom, #000 40%, transparent 100%);
        opacity: 0.28;
        pointer-events: none;
        position: absolute;
      }
    `}

  ${({ $glass }) =>
    $glass &&
    `
      &::after {
        background-image: url("${$glass}");
        background-position: top right;
        background-repeat: no-repeat;
        background-size: contain;
        content: "";
        height: 84px;
        opacity: 0.7;
        pointer-events: none;
        position: absolute;
        right: 12px;
        top: 8px;
        width: 84px;
      }
    `}

  /* Rows ride above the artwork. */
  > * {
    position: relative;
    z-index: 1;
  }
`;
